import { createFileRoute } from "@tanstack/react-router";
import {
  Sparkles,
  Send,
  CalendarClock,
  LinkIcon,
  BarChart3,
  Upload,
  Wand2,
  ShieldCheck,
  Clock,
} from "lucide-react";

export const Route = createFileRoute("/admin/social/guide")({
  component: GuidePage,
  ssr: false,
  head: () => ({ meta: [{ title: "How to use · Social media" }] }),
});

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-foreground">
        <Icon className="h-5 w-5 text-primary" />
        {title}
      </h2>
      <div className="space-y-2 text-sm text-muted-foreground [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline">
        {children}
      </div>
    </section>
  );
}

function GuidePage() {
  return (
    <div className="max-w-3xl space-y-5">
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-5">
        <h1 className="font-display text-2xl font-bold text-foreground">
          How to use Social media
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Everything you need to publish, schedule, and grow Simply Approved Business Loans on
          Facebook, Instagram, LinkedIn, X/Twitter, and TikTok — without leaving this dashboard.
        </p>
      </div>

      <Section icon={LinkIcon} title="1. Connect your accounts (one-time)">
        <p>Open the <strong>Accounts</strong> tab and click <strong>Connect</strong> next to each platform you want to publish to. You'll be sent to that platform's login, then back here.</p>
        <p>You can connect multiple accounts per platform (e.g. a Page + a Group). Anything connected becomes a publish target everywhere else in this dashboard.</p>
      </Section>

      <Section icon={Send} title="2. Compose a post manually">
        <p>Use the <strong>Compose</strong> tab to write a one-off post.</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>Type your content (max ~5,000 chars — platforms will truncate on their own limits).</li>
          <li>Paste an image or video URL (.jpg, .png, .mp4). Videos route through each platform's video API automatically.</li>
          <li>Pick which connected accounts to publish to.</li>
          <li>Click <strong>Post now</strong> or set a date/time to schedule it.</li>
        </ul>
      </Section>

      <Section icon={Sparkles} title="3. Let the AI draft posts for you (daily)">
        <p>Every morning at <strong>8:00 AM ET</strong> our agent writes 3 fresh posts and saves them to the <strong>AI drafts</strong> tab. Each post is:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>Grounded in a real query clients are searching for (from Google Search Console).</li>
          <li>Tied to a real product page on our site, with that page's URL as the CTA.</li>
          <li>Written in our brand voice — plain English, regulator-safe, no "guaranteed approval", no invented rates.</li>
        </ul>
        <p>You can also click <strong>Generate now</strong> on the AI drafts tab anytime to get 3 more on demand.</p>
      </Section>

      <Section icon={Wand2} title="4. Talk to the AI to fix a draft">
        <p>On any draft card, scroll to the <strong>Talk to the AI</strong> box. Type an instruction like:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>"Make it shorter and more punchy."</li>
          <li>"Fix the CTA, no emojis."</li>
          <li>"Rewrite for restaurant owners specifically."</li>
        </ul>
        <p>Hit <strong>Redo</strong>. The AI rewrites the post in-place and saves it. Keep iterating until you love it, then post or schedule.</p>
      </Section>

      <Section icon={CalendarClock} title="5. Schedule + the Calendar view">
        <p>Both Compose and AI drafts let you pick a date/time instead of posting now. Anything scheduled shows up on the <strong>Calendar</strong> tab — drag, edit, or delete from there.</p>
        <p>Scheduled posts fire automatically; you don't need to keep this tab open.</p>
      </Section>

      <Section icon={Upload} title="6. Bulk upload from CSV">
        <p>Got 50 posts in a spreadsheet? Use the <strong>Bulk CSV</strong> tab. Required columns: <code>content</code>, <code>scheduled_at</code> (ISO date), <code>platforms</code> (comma-separated), and optional <code>media_url</code>.</p>
      </Section>

      <Section icon={BarChart3} title="7. See what's working">
        <p>The <strong>Analytics</strong> tab pulls impressions, engagement, and clicks from each connected platform. Use it to spot which products + which CTAs drive the most page visits, then tell the AI to write more like that.</p>
      </Section>

      <Section icon={ShieldCheck} title="Brand & compliance rules the AI follows">
        <ul className="ml-5 list-disc space-y-1">
          <li>Brand name is always <strong>Simply Approved Business Loans</strong>.</li>
          <li>Never promises guaranteed approval, specific rates, or specific funding amounts.</li>
          <li>Educational first, sell second. No hype, no emoji stacks, no ALL CAPS.</li>
          <li>Every post ends with a link to a real product page on our site.</li>
          <li>2–4 relevant hashtags max.</li>
        </ul>
      </Section>

      <Section icon={Clock} title="Quick troubleshooting">
        <ul className="ml-5 list-disc space-y-1">
          <li><strong>"No connected accounts yet"</strong> — go to the Accounts tab and connect at least one.</li>
          <li><strong>AI drafts are repetitive</strong> — confirm Google Search Console is connected under SEO settings; otherwise the agent only rotates products.</li>
          <li><strong>Post failed to publish</strong> — usually means the platform's token expired. Re-connect that account on the Accounts tab.</li>
          <li><strong>Video didn't upload</strong> — must be a direct <code>.mp4</code> URL, under each platform's size/length limit.</li>
        </ul>
      </Section>
    </div>
  );
}
